import React, { useState, useEffect, useCallback } from 'react';
import { UserPlusIcon, KeyIcon, PencilIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';
import PromptDialog from '../components/common/PromptDialog';
import PermissionGate from '../components/common/PermissionGate';
import Modal from '../components/common/Modal';
import { PERMISSION_GROUPS, ADMIN_ROLES } from '../constants/permissions';

// ── helpers ────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  role: 'admin',
  permissions: [],
};

const generatePassword = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  // Rejection sampling: discard values >= floor(256/chars.length)*chars.length to eliminate modulo bias
  const limit = Math.floor(256 / chars.length) * chars.length;
  const result = [];
  while (result.length < 12) {
    const buf = new Uint8Array(24);
    crypto.getRandomValues(buf);
    for (const v of buf) {
      if (v < limit) result.push(chars[v % chars.length]);
      if (result.length === 12) break;
    }
  }
  return result.join('');
};

// ── main component ─────────────────────────────────────────────────────────

const Admins = () => {
  const { admin: currentAdmin } = useAuth();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  // Deactivate / reactivate
  const [confirmStatus, setConfirmStatus] = useState(null); // { admin, newStatus }
  const [settingStatus, setSettingStatus] = useState(false);

  // Edit permissions
  const [editTarget, setEditTarget] = useState(null); // admin object
  const [editForm, setEditForm] = useState({ role: '', permissions: [] });
  const [saving, setSaving] = useState(false);

  // Reset password
  const [resetTarget, setResetTarget] = useState(null); // admin object
  const [resetting, setResetting] = useState(false);

  // ── data ──────────────────────────────────────────────────────────────────

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await api.get('/admin/admins');
      setAdmins(res.data.data);
    } catch {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const isSelf = (id) => String(id) === String(currentAdmin?._id);

  // ── create ────────────────────────────────────────────────────────────────

  const toggleCreatePermission = (perm) => {
    setCreateForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/admins', createForm);
      toast.success('Admin created');
      setShowCreate(false);
      setCreateForm(EMPTY_FORM);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setCreating(false);
    }
  };

  // ── status toggle ─────────────────────────────────────────────────────────

  const handleSetStatus = async () => {
    if (!confirmStatus) return;
    setSettingStatus(true);
    try {
      await api.put(`/admin/admins/${confirmStatus.admin._id}/status`, {
        status: confirmStatus.newStatus,
      });
      toast.success(`Admin ${confirmStatus.newStatus === 'active' ? 'reactivated' : 'deactivated'}`);
      setConfirmStatus(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSettingStatus(false);
    }
  };

  // ── edit permissions ──────────────────────────────────────────────────────

  const openEdit = (admin) => {
    setEditTarget(admin);
    setEditForm({ role: admin.role, permissions: [...(admin.permissions || [])] });
  };

  const toggleEditPermission = (perm) => {
    setEditForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/admins/${editTarget._id}/permissions`, editForm);
      toast.success('Permissions updated');
      setEditTarget(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  // ── reset password ────────────────────────────────────────────────────────

  const handleResetPassword = async ({ newPassword }) => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setResetting(true);
    try {
      await api.post(`/admin/admins/${resetTarget._id}/reset-password`, { newPassword });
      toast.success(`Password reset — new password: ${newPassword}`, { autoClose: 10000 });
      setResetTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner />;

  return (
    <PermissionGate
      permission="manage_admins"
      fallback={
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">You do not have permission to manage admins.</p>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">Admin Accounts</h1>
          <button
            className="btn-primary flex items-center gap-2"
            onClick={() => setShowCreate(true)}
          >
            <UserPlusIcon className="w-5 h-5" />
            New Admin
          </button>
        </div>

        {/* Admins table */}
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Name</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Role</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Permissions</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {admins.map((a) => (
                  <tr key={a._id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">
                      {a.firstName} {a.lastName}
                      {isSelf(a._id) && (
                        <span className="ml-2 text-xs text-indigo-500">(you)</span>
                      )}
                    </td>
                    <td className="table-cell text-gray-500">{a.email}</td>
                    <td className="table-cell">
                      <span className="capitalize text-sm">{a.role.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={a.status} />
                    </td>
                    <td className="table-cell">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(a.permissions || []).slice(0, 3).map((p) => (
                          <span
                            key={p}
                            className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs"
                          >
                            {p.replace(/_/g, ' ')}
                          </span>
                        ))}
                        {(a.permissions || []).length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                            +{a.permissions.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        {!isSelf(a._id) && (
                          <button
                            onClick={() => openEdit(a)}
                            className="btn-secondary flex items-center gap-1 text-xs py-1 px-2"
                            title="Edit permissions"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                            Edit
                          </button>
                        )}
                        {!isSelf(a._id) && (
                          <button
                            onClick={() => setResetTarget(a)}
                            className="btn-secondary flex items-center gap-1 text-xs py-1 px-2"
                            title="Reset password"
                          >
                            <KeyIcon className="w-3.5 h-3.5" />
                            Reset PW
                          </button>
                        )}
                        {!isSelf(a._id) && (
                          a.status === 'active' ? (
                            <button
                              onClick={() => setConfirmStatus({ admin: a, newStatus: 'inactive' })}
                              className="btn-danger text-xs py-1 px-2"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => setConfirmStatus({ admin: a, newStatus: 'active' })}
                              className="btn-primary text-xs py-1 px-2"
                            >
                              Reactivate
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create admin modal */}
        <Modal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          title="Create Admin Account"
          footer={
            <>
              <button onClick={() => setShowCreate(false)} className="btn-secondary" disabled={creating}>
                Cancel
              </button>
              <button type="submit" form="create-admin-form" className="btn-primary disabled:opacity-50" disabled={creating}>
                {creating ? 'Creating…' : 'Create Admin'}
              </button>
            </>
          }
        >
          <form id="create-admin-form" onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input className="input" required value={createForm.firstName}
                  onChange={(e) => setCreateForm((p) => ({ ...p, firstName: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input className="input" required value={createForm.lastName}
                  onChange={(e) => setCreateForm((p) => ({ ...p, lastName: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" className="input" required value={createForm.email}
                onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="flex gap-2">
                <input type="text" className="input flex-1" required minLength={8}
                  value={createForm.password}
                  onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))} />
                <button type="button" className="btn-secondary whitespace-nowrap"
                  onClick={() => setCreateForm((p) => ({ ...p, password: generatePassword() }))}>
                  Generate
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select className="input" value={createForm.role}
                onChange={(e) => setCreateForm((p) => ({ ...p, role: e.target.value }))}>
                {ADMIN_ROLES.map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
              <div className="space-y-3">
                {PERMISSION_GROUPS.map((group) => (
                  <div key={group.label}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {group.label}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.permissions.map((perm) => (
                        <label key={perm} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input type="checkbox"
                            checked={createForm.permissions.includes(perm)}
                            onChange={() => toggleCreatePermission(perm)}
                            className="rounded border-gray-300 text-indigo-600" />
                          {perm.replace(/_/g, ' ')}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        </Modal>

        {/* Edit permissions modal */}
        <Modal
          isOpen={!!editTarget}
          onClose={() => setEditTarget(null)}
          title={`Edit — ${editTarget?.firstName} ${editTarget?.lastName}`}
          footer={
            <>
              <button onClick={() => setEditTarget(null)} className="btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSavePermissions} className="btn-primary disabled:opacity-50" disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select className="input" value={editForm.role}
                onChange={(e) => setEditForm((p) => ({ ...p, role: e.target.value }))}>
                {ADMIN_ROLES.map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
              <div className="space-y-3">
                {PERMISSION_GROUPS.map((group) => (
                  <div key={group.label}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {group.label}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.permissions.map((perm) => (
                        <label key={perm} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input type="checkbox"
                            checked={editForm.permissions.includes(perm)}
                            onChange={() => toggleEditPermission(perm)}
                            className="rounded border-gray-300 text-indigo-600" />
                          {perm.replace(/_/g, ' ')}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>

        {/* Deactivate / reactivate confirm */}
        <ConfirmDialog
          isOpen={!!confirmStatus}
          title={confirmStatus?.newStatus === 'inactive' ? 'Deactivate Admin' : 'Reactivate Admin'}
          message={
            confirmStatus?.newStatus === 'inactive'
              ? `Deactivate ${confirmStatus?.admin?.firstName} ${confirmStatus?.admin?.lastName}? Their active sessions will be terminated immediately.`
              : `Reactivate ${confirmStatus?.admin?.firstName} ${confirmStatus?.admin?.lastName}?`
          }
          confirmLabel={confirmStatus?.newStatus === 'inactive' ? 'Deactivate' : 'Reactivate'}
          variant={confirmStatus?.newStatus === 'inactive' ? 'danger' : 'primary'}
          loading={settingStatus}
          onConfirm={handleSetStatus}
          onCancel={() => setConfirmStatus(null)}
        />

        {/* Reset password prompt */}
        <PromptDialog
          isOpen={!!resetTarget}
          title={`Reset Password — ${resetTarget?.firstName} ${resetTarget?.lastName}`}
          fields={[{
            name: 'newPassword',
            label: 'New Password',
            type: 'text',
            placeholder: 'Min 8 characters',
            required: true,
          }]}
          confirmLabel="Reset Password"
          loading={resetting}
          onConfirm={handleResetPassword}
          onCancel={() => setResetTarget(null)}
        />
      </div>
    </PermissionGate>
  );
};

export default Admins;
